import { useState, useEffect } from 'react';
import { Shield, AlertTriangle, ExternalLink, ChevronDown, Eye, CheckCircle2, XCircle, Clock, Filter } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { adminListReports, adminUpdateReport } from '../../services/api';
import AdminDashboardNav from '../../components/dashboard/AdminDashboardNav';

interface Report {
  id: number;
  tenantId: number;
  reporterEmail: string;
  reason: string;
  evidence: string | null;
  pageUrl: string;
  severityScore: number;
  status: string;
  createdAt: number;
  resolvedAt: number | null;
  adminNotes: string | null;
  artistName: string;
  artistSlug: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-red-500/20 text-red-400 border-red-500/30',
  reviewing: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
  dismissed: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

const SEVERITY_COLORS = (score: number) => {
  if (score >= 85) return 'text-red-400';
  if (score >= 70) return 'text-orange-400';
  if (score >= 50) return 'text-yellow-400';
  return 'text-gray-400';
};

export default function AdminCompliancePage() {
  const { token } = useAuthStore();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [statusFilter, setStatusFilter] = useState('open');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const loadReports = async () => {
    setLoading(true);
    try {
      const res = await adminListReports({ page: pagination.page, status: statusFilter }, token!);
      if (res.ok) {
        const json = await res.json() as { data: Report[]; pagination: Pagination };
        setReports(json.data);
        setPagination(json.pagination);
      }
    } catch (err) {
      console.error('[Compliance] Failed to load reports:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [statusFilter, pagination.page]);

  const handleStatusChange = async (reportId: number, newStatus: string) => {
    setUpdatingId(reportId);
    try {
      const res = await adminUpdateReport(reportId, { status: newStatus }, token!);
      if (res.ok) {
        setReports((prev) => prev.map((r) => r.id === reportId ? { ...r, status: newStatus } : r));
      }
    } catch (err) {
      console.error('[Compliance] Failed to update report:', err);
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (ts: number) => new Date(ts * 1000).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="min-h-screen bg-deep-charcoal">
      <AdminDashboardNav currentPage="compliance" />

      <main className="pt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-electric-cyan" />
            <div>
              <h1 className="text-2xl font-bold">Compliance Dashboard</h1>
              <p className="text-sm text-text-secondary font-mono">Reports sorted by severity</p>
            </div>
          </div>
          <span className="text-xs font-mono text-text-muted">
            {pagination.total} total reports
          </span>
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-2 mb-6">
          <Filter className="w-4 h-4 text-text-muted" />
          {['open', 'reviewing', 'resolved', 'dismissed', 'all'].map((f) => (
            <button
              key={f}
              onClick={() => { setStatusFilter(f); setPagination((p) => ({ ...p, page: 1 })); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider transition-all ${
                statusFilter === f
                  ? 'bg-electric-cyan/10 text-electric-cyan border border-electric-cyan/30'
                  : 'bg-white/5 text-text-secondary border border-white/10 hover:border-white/20'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Reports list */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-electric-cyan border-t-transparent rounded-full animate-spin" />
          </div>
        ) : reports.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Shield className="w-12 h-12 text-green-400/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No reports</h3>
            <p className="text-text-secondary text-sm">No {statusFilter !== 'all' ? statusFilter : ''} compliance reports found.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((report) => {
              const isExpanded = expandedId === report.id;
              return (
                <div key={report.id} className="glass-card overflow-hidden">
                  {/* Summary row */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : report.id)}
                    className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/5 transition-colors"
                  >
                    {/* Severity badge */}
                    <div className={`text-center shrink-0 ${SEVERITY_COLORS(report.severityScore)}`}>
                      <span className="text-xl font-bold font-mono">{report.severityScore}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono border ${STATUS_COLORS[report.status] || STATUS_COLORS.open}`}>
                          {report.status.toUpperCase()}
                        </span>
                        <span className="text-sm font-semibold text-white truncate">{report.reason}</span>
                        <span className="text-xs text-text-muted">— {report.artistName}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-text-muted">
                        <span>ID#{report.id}</span>
                        <span>{formatDate(report.createdAt)}</span>
                        <span>{report.reporterEmail}</span>
                      </div>
                    </div>

                    {/* View URL shortcut */}
                    <a
                      href={report.pageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-electric-cyan hover:bg-white/10 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      View URL
                    </a>

                    <ChevronDown className={`w-4 h-4 text-text-muted shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="border-t border-white/5 px-5 py-4 bg-white/[0.02]">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <span className="text-xs font-mono text-text-muted uppercase tracking-wider">Reporter</span>
                          <p className="text-sm text-white mt-1">{report.reporterEmail}</p>
                        </div>
                        <div>
                          <span className="text-xs font-mono text-text-muted uppercase tracking-wider">Artist</span>
                          <p className="text-sm text-white mt-1">{report.artistName} ({report.artistSlug})</p>
                        </div>
                        <div className="md:col-span-2">
                          <span className="text-xs font-mono text-text-muted uppercase tracking-wider">Page URL</span>
                          <p className="text-sm mt-1">
                            <a href={report.pageUrl} target="_blank" rel="noopener noreferrer" className="text-electric-cyan hover:underline break-all">
                              {report.pageUrl}
                            </a>
                          </p>
                        </div>
                        {report.evidence && (
                          <div className="md:col-span-2">
                            <span className="text-xs font-mono text-text-muted uppercase tracking-wider">Evidence</span>
                            <p className="text-sm text-white/80 mt-1 whitespace-pre-wrap bg-white/5 rounded-lg p-3 border border-white/5">
                              {report.evidence}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 pt-3 border-t border-white/5">
                        <span className="text-xs font-mono text-text-muted mr-2">Set status:</span>
                        {['open', 'reviewing', 'resolved', 'dismissed'].map((s) => (
                          <button
                            key={s}
                            disabled={report.status === s || updatingId === report.id}
                            onClick={() => handleStatusChange(report.id, s)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider transition-all border disabled:opacity-30 ${
                              report.status === s
                                ? STATUS_COLORS[s]
                                : 'bg-white/5 text-text-secondary border-white/10 hover:border-white/20'
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              disabled={pagination.page <= 1}
              onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
              className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-sm disabled:opacity-30 hover:bg-white/10 transition-colors"
            >
              Previous
            </button>
            <span className="text-xs font-mono text-text-muted">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-sm disabled:opacity-30 hover:bg-white/10 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
